import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import AppShell from './components/layout/AppShell'
import AuthShell from './components/layout/AuthShell'
import ProtectedRoute from './components/auth/ProtectedRoute'
import SignIn from './routes/SignIn'
import Home from './routes/Home'
import PartnerDetail from './routes/PartnerDetail'
import OrderWizard from './routes/OrderWizard'
import OrderSuccess from './routes/OrderSuccess'
import Orders from './routes/Orders'
import OrderDetail from './routes/OrderDetail'
import Settings from './routes/Settings'
import PinLocation from './routes/PinLocation'
import PriceCheck from './routes/PriceCheck'
import OpenInBrowser from './components/common/OpenInBrowser'
import NotFound from './routes/NotFound'

const protect = (element) => <ProtectedRoute>{element}</ProtectedRoute>

/**
 * One URL, two audiences.
 *
 * `/orders/:orderId` is the customer's own order screen. With `?t=<trackToken>` on the
 * end it is the same screen shown to whoever they passed the link to — same route, same
 * component, no second page. See docs/intent/public-order-tracking.md.
 *
 * The branch has to happen here rather than inside `OrderDetail`, because the two
 * versions differ in what wraps them:
 *
 *   - **Signed in** — `ProtectedRoute` and the app chrome, exactly as before.
 *   - **Shared** — neither. `ProtectedRoute` would bounce a visitor with no account to
 *     sign-in, which is the whole feature failing; and `AppShell`'s header and bottom
 *     bar are links to Home, Orders and Settings, every one of which leads a person
 *     without an account to a sign-in screen. A navigation bar where nothing is
 *     reachable is worse than no navigation bar.
 *
 * The page between them is identical, which is the part that was asked for.
 */
function OrderDetailRoute() {
  const [params] = useSearchParams()
  const shareToken = params.get('t')

  if (shareToken) {
    /**
     * The column, without the chrome.
     *
     * `AppShell` carried three things: a header, a bottom bar, and the 640px column that
     * stops the page running edge to edge on anything wider than a phone. The first two
     * are navigation to places this visitor cannot go. The third is not navigation at
     * all — it is the layout every other screen in the app is written for, and dropping
     * it with the rest left the shared view stretched across a desktop window.
     */
    return (
      <div
        className="min-h-screen min-h-[100dvh] bg-surface-2"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <main className="w-full max-w-[640px] mx-auto pb-8">
          <OrderDetail shareToken={shareToken} />
        </main>
      </div>
    )
  }

  return <AppShell>{protect(<OrderDetail />)}</AppShell>
}

// Everything that needs an account. Split out so `AuthProvider` can be mounted *inside*
// the router rather than around it — see the tracking route below.
function Authenticated() {
  return (
    <AuthProvider>
      <Routes>
        {/* Nothing to navigate to yet, so these keep the centred card. */}
        <Route element={<AuthShell />}>
          <Route path="/" element={<SignIn />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route element={<AppShell />}>
          <Route path="/home" element={protect(<Home />)} />
          <Route path="/partners/:slug" element={protect(<PartnerDetail />)} />
          <Route path="/order/:slug" element={protect(<OrderWizard />)} />
          <Route path="/order/:slug/success" element={protect(<OrderSuccess />)} />
          <Route path="/orders" element={protect(<Orders />)} />
          {/* Profile folded into Account; the old path still resolves for saved links. */}
          <Route path="/profile" element={<Navigate to="/settings" replace />} />
          <Route path="/settings" element={protect(<Settings />)} />
        </Route>

        {/* Outside the `AppShell` group because it renders its own — see
            `OrderDetailRoute`, which has to choose the wrapper before the page. */}
        <Route path="/orders/:orderId" element={<OrderDetailRoute />} />

        <Route path="/dashboard" element={<Navigate to="/home" replace />} />
      </Routes>
    </AuthProvider>
  )
}

export default function App() {
  return (
    <>
      {/**
        * Above the router, so it is on every screen this app has.
        *
        * A customer arriving from a Messenger thread is in Facebook's browser, and Google
        * refuses OAuth inside an embedded webview — so signing in cannot be made to work
        * there. `services/auth.js` has known that for a while and said it only *after* the
        * attempt failed, at the end of a form they had already filled in.
        *
        * Out here it is said before any of that, once, and it renders nothing at all in an
        * ordinary browser — which is every visit that is not from a social app.
        */}
      <OpenInBrowser />

      <Routes>
      {/*
        Opened from a button in a Messenger thread, by somebody with no account, and
        mounted outside `AuthProvider` on purpose — that is the whole guarantee. Under
        the provider it would still work today, because the provider no-ops without a
        stored token, but "works because a provider happens to return early" is a
        guarantee one refactor away from a customer seeing a sign-in screen. Out here
        there is no auth code on the path to run.

        `/track/:token` used to sit beside this for the same reason. It is gone: the
        shared link is now `/orders/:orderId?t=`, which cannot live out here because the
        same URL without a token is the customer's own signed-in screen. What replaces
        the guarantee is `OrderDetailRoute`, which chooses the wrapper before the page
        and gives the shared view neither `ProtectedRoute` nor the app chrome.
      */}
      <Route path="/pin" element={<PinLocation />} />

      {/*
        The public price checker, out here for the same reason `/pin` is: it is opened by a
        shop with no account, often from inside Messenger, and being asked to sign in
        before being told a price is the thing that stopped them asking. Pressing *Ituloy*
        on it navigates into `/order/:slug`, which is protected — the sign-in happens
        there, at the moment it is actually needed, carrying the route in the query string.
      */}
      <Route path="/presyo/:slug" element={<PriceCheck />} />

        <Route path="/*" element={<Authenticated />} />
      </Routes>
    </>
  )
}
